'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import blogs from '../../../../data/blogs';

export default function BlogSearch({
  searchQuery,
  selectedCategory,
  dateRange,
  onSearch,
  onCategoryChange,
  onDateRangeChange,
  onResetFilters,
}) {
  const [categories, setCategories] = useState([]);
  const [isSticky, setIsSticky] = useState(false);
  const [startDate, endDate] = dateRange;

  const toTitleCase = (str) =>
    str
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  useEffect(() => {
    // Extract unique categories
    const uniqueCategories = [
      'All',
      ...new Set(blogs.map((blog) => blog.category)),
    ];

    const formattedCategories = uniqueCategories.map((category) => ({
      original: category,
      display:
        category === 'All'
          ? 'All'
          : category.split(', ').map(toTitleCase).sort().join(', '),
    }));

    setCategories(formattedCategories);

    // Handle sticky behavior
    const handleScroll = () => {
      setIsSticky(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      viewport={{ once: true }}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-12 transition-all duration-300 ${
        isSticky ? 'sticky top-20 z-30 shadow-2xl' : ''
      }`}
    >
      <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
        {/* Search input */}
        <div className='relative'>
          <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
            <svg
              className='w-5 h-5 text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
              ></path>
            </svg>
          </div>
          <input
            type='text'
            placeholder='Search blogs...'
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            className='w-full pl-10 pr-4 py-3 border dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primarycolor focus:border-primarycolor transition-all dark:bg-gray-700 dark:text-white'
          />
          {searchQuery && (
            <button
              onClick={() => onSearch('')}
              className='absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
            >
              <svg
                className='w-5 h-5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M6 18L18 6M6 6l12 12'
                ></path>
              </svg>
            </button>
          )}
        </div>

        {/* Category select */}
        <div className='relative'>
          <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
            <svg
              className='w-5 h-5 text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z'
              ></path>
            </svg>
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => onCategoryChange(e.target.value)}
            className='w-full pl-10 pr-10 py-3 border dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primarycolor focus:border-primarycolor appearance-none transition-all dark:bg-gray-700 dark:text-white'
          >
            {categories.map((category) => (
              <option key={category.original} value={category.display}>
                {category.display}
              </option>
            ))}
          </select>
          <div className='absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none'>
            <svg
              className='w-5 h-5 text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M19 9l-7 7-7-7'
              ></path>
            </svg>
          </div>
        </div>

        {/* Date picker */}
        <div className='relative'>
          <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none'>
            <svg
              className='w-5 h-5 text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
              ></path>
            </svg>
          </div>
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => onDateRangeChange(update)}
            isClearable
            placeholderText='Select Date Range'
            className='w-full pl-10 pr-10 py-3 border dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primarycolor focus:border-primarycolor transition-all dark:bg-gray-700 dark:text-white'
          />
          {(startDate || endDate) && (
            <button
              onClick={() => onDateRangeChange([null, null])}
              className='absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
            >
              <svg
                className='w-5 h-5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M6 18L18 6M6 6l12 12'
                ></path>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Reset filters button */}
      {(searchQuery || selectedCategory !== 'All' || startDate || endDate) && (
        <div className='flex justify-end mt-4'>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onResetFilters}
            className='flex items-center gap-2 px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors'
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
              ></path>
            </svg>
            Reset Filters
          </motion.button>
        </div>
      )}
    </motion.div>
  );
}
